/**
 * useGemscape.js
 *
 * React hook that manages the three-tier lifecycle of a gemscape SVG:
 *
 *   Static  → the track renders a plain <image href=...> (zero JS cost)
 *   Primed  → the SVG has been fetched, parsed & cached in memory (no DOM cost)
 *   Active  → the SVG is rendered inline with classifiable shape DOM nodes
 *
 * Transition rules:
 *   - Static → Primed: when the Waveform enters (or approaches) the viewport
 *   - Primed → Active: when the track is hovered or playing
 *   - Active → Primed: when the track is un-hovered and not playing
 *
 * The hook returns:
 *   { tier, markup, viewBox }
 *
 *   tier     "static" | "primed" | "active"
 *   markup   string | null — the sanitized SVG inner HTML (only set when active)
 *   viewBox  string | null — the SVG viewBox
 */
import { useState, useEffect } from "react";
import { getGemscape, isCached } from "../gemscape-cache";

// Shared IntersectionObserver (one for all Waveform instances)
let observer = null;
const observerCallbacks = new Map(); // element → callback

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const cb = observerCallbacks.get(entry.target);
        if (cb) cb(entry.isIntersecting);
      });
    },
    {
      // Start prefetching when the element is within 200px of the viewport
      rootMargin: "200px"
    }
  );

  return observer;
}

function observe(element, callback) {
  observerCallbacks.set(element, callback);
  getObserver().observe(element);
}

function unobserve(element) {
  observerCallbacks.delete(element);
  if (observer) observer.unobserve(element);
}

export function useGemscape({ url, hovered, isPlaying, containerRef }) {
  const [gemscapeData, setGemscapeData] = useState(null);

  // ── Prefetch when entering viewport ───────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !url) return;

    let cancelled = false;

    // If it's already cached, load data immediately
    if (isCached(url)) {
      getGemscape(url).then(data => {
        if (cancelled || !data) return;
        setGemscapeData(data);
      });
    }

    observe(el, intersecting => {
      if (cancelled) return;
      if (intersecting && url) {
        // Once the fetch completes, data is available for activation
        getGemscape(url).then(data => {
          if (cancelled || !data) return;
          setGemscapeData(data);
        });
      }
    });

    return () => {
      cancelled = true;
      unobserve(el);
    };
  }, [url, containerRef]);

  // ── Deferred isPlaying flag ─────────────────────────────────────────
  // When playback starts, the Player mounts an <audio> element on the same
  // frame.  If we also mount the heavy inline SVG (active tier) on that
  // same frame, the two compete and delay audible playback.  So we defer
  // the isPlaying signal by one rAF — the audio element gets a clear first
  // frame, then the gemscape upgrades to active on the next.
  // Hover is still synchronous so cursor interaction has zero lag.
  const [deferredPlaying, setDeferredPlaying] = useState(false);
  useEffect(() => {
    if (isPlaying && !deferredPlaying) {
      const id = requestAnimationFrame(() => setDeferredPlaying(true));
      return () => cancelAnimationFrame(id);
    }
    if (!isPlaying && deferredPlaying) {
      setDeferredPlaying(false);
    }
  }, [isPlaying, deferredPlaying]);

  // ── Derive tier synchronously — no useEffect lag ──────────────────
  // Hover → active is instant (fixes cursor tracking bugs).
  // isPlaying → active is deferred by one frame (keeps audio snappy).
  const tier = !gemscapeData
    ? "static"
    : hovered || deferredPlaying
    ? "active"
    : "primed";

  return {
    tier,
    markup: tier === "active" ? gemscapeData?.markup : null,
    viewBox: gemscapeData?.viewBox || null
  };
}
